import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { SectionHeader } from "./SectionHeader";
import { FactorControls } from "./FactorControls";

interface SearchVolumeScoringCardProps {
  searchVolumeWeight: number;
  onSearchVolumeWeightChange: (v: number) => void;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
}

const SEARCH_VOLUME_ROWS = [
  { label: "≤ 100", volume: 100 },
  { label: "1,000", volume: 1000 },
  { label: "2,500", volume: 2500 },
  { label: "5,000", volume: 5000 },
  { label: "8,000", volume: 8000 },
  { label: "≥ 10,000", volume: 10000 },
] as const;

export function SearchVolumeScoringCard({
  searchVolumeWeight,
  onSearchVolumeWeightChange,
  enabled,
  onEnabledChange,
}: SearchVolumeScoringCardProps) {
  return (
    <Card id="kps-module-search-volume" className={cn(!enabled && "bg-muted/30 order-1")}>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <SectionHeader
            title="Search Volume Scoring"
            description={`Points scale linearly with monthly search volume, up to 10,000. Adjust the scoring weight to make changes to the points earned below.`}
          />
          <FactorControls
            id="kps-toggle-search-volume"
            enabled={enabled}
            onEnabledChange={onEnabledChange}
            value={searchVolumeWeight}
            onValueChange={onSearchVolumeWeightChange}
          />
        </div>

        <div className={cn("space-y-4", !enabled && "opacity-50 pointer-events-none select-none")}>
          <Alert variant="default">
            <AlertDescription className="space-y-1">
              <p className="text-sm font-semibold text-foreground">Formula</p>
              <p className="text-base tabular-nums text-foreground">
                <span className="text-muted-foreground">score = </span>
                <span className="font-semibold">min(volume, 10,000)</span>
                <span className="text-muted-foreground"> ÷ </span>
                <span className="font-semibold">10,000</span>
                <span className="text-muted-foreground"> × </span>
                <span className="font-semibold tabular-nums">{searchVolumeWeight}</span>
              </p>
            </AlertDescription>
          </Alert>

          <div className="space-y-0">
            <div className="grid grid-cols-[180px_1fr_100px] gap-4 items-center px-1 h-9 border-b border-border">
              <span className="text-xs font-semibold text-foreground">Monthly Search Volume</span>
              <span className="text-xs font-semibold text-foreground text-center">Points Earned</span>
              <span className="text-xs font-semibold text-foreground text-right">Score</span>
            </div>
            {SEARCH_VOLUME_ROWS.map((row) => {
              const max = searchVolumeWeight;
              const score = (Math.min(row.volume, 10000) / 10000) * max;
              const pct = max > 0 ? (score / max) * 100 : 0;
              const formatted = Number.isInteger(score) ? score.toString() : score.toFixed(2).replace(/\.?0+$/, "");
              return (
                <div key={row.label} className="grid grid-cols-[180px_1fr_100px] gap-4 items-center px-1 py-3 border-b border-border last:border-b-0">
                  <span className="text-sm text-foreground tabular-nums">{row.label}</span>
                  <Progress value={pct} className="h-2" indicatorClassName="bg-primary" />
                  <span className="text-sm tabular-nums text-right">
                    <span className="text-foreground">{formatted}</span>
                    <span className="text-xs text-muted-foreground"> / {max}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
